import { useCallback, useEffect, useMemo, useState } from 'react'

import ClientApplication from 'api/application/interfaces/ClientApplication'
import { fetchJson } from 'api/fetcher'
import useSwr, { useSWRConfig } from 'swr'

export interface ApplicationIdentity {
    readonly routeIdentifier: string
    readonly numericId: number
    readonly uuid: string
}

export const resolveApplicationIdentity = (
    routeIdentifier: string | undefined,
    clientApplication: ClientApplication | undefined,
): ApplicationIdentity | undefined => {
    if (!routeIdentifier || !clientApplication) {
        return undefined
    }

    return {
        routeIdentifier,
        numericId: clientApplication.id,
        uuid: clientApplication.uuid,
    }
}

export const getClientApplicationSwrKey = (
    applicationId: string | undefined,
) => (applicationId ? `v3/client-application/${applicationId}/` : null)

export interface UseClientApplicationInterface {
    applicationIdentity?: ApplicationIdentity
    clientApplication: ClientApplication | undefined
    loading: boolean
    error: Error | null
}

export interface UseClientApplicationOptions {
    readonly revalidateIfStale?: boolean
}

// Track request activity per session cache so aliases can replace old data while
// preserving canonical requests that are in flight or completed during the lookup.
const requestRevisions = new WeakMap<object, Map<string, number>>()
let nextRequestRevision = 0

const useClientApplication = (
    routeIdentifier: string | undefined,
    { revalidateIfStale = true }: UseClientApplicationOptions = {},
): UseClientApplicationInterface => {
    const { cache, mutate } = useSWRConfig()

    const revisions = useMemo(() => {
        let cacheRevisions = requestRevisions.get(cache)

        if (!cacheRevisions) {
            cacheRevisions = new Map<string, number>()
            requestRevisions.set(cache, cacheRevisions)
        }

        return cacheRevisions
    }, [cache])

    // Reopened routes start on the UUID resource so their refresh errors stay
    // on the same cache entry the hook observes after resolving the identity.
    const initialApplicationIdentifier = useMemo(() => {
        const routeKey = getClientApplicationSwrKey(routeIdentifier)

        const cachedApplication = routeKey
            ? (cache.get(routeKey)?.data as ClientApplication | undefined)
            : undefined

        return cachedApplication?.uuid ?? routeIdentifier
    }, [cache, routeIdentifier])

    const [resolvedIdentity, setResolvedIdentity] =
        useState<ApplicationIdentity>()

    const applicationIdentifier =
        resolvedIdentity && resolvedIdentity.routeIdentifier === routeIdentifier
            ? resolvedIdentity.uuid
            : initialApplicationIdentifier

    const fetchClientApplication = useCallback(
        async (key: string): Promise<ClientApplication> => {
            const requestRevision = ++nextRequestRevision

            revisions.set(key, requestRevision)

            const response = await fetchJson<ClientApplication>(key)

            revisions.set(key, ++nextRequestRevision)
            const canonicalKey = getClientApplicationSwrKey(response.uuid)

            // Existing data alone may be stale. Only an active canonical
            // request or one that finished during this lookup takes precedence.
            if (
                canonicalKey &&
                canonicalKey !== key &&
                !cache.get(canonicalKey)?.isValidating &&
                (revisions.get(canonicalKey) ?? 0) < requestRevision
            ) {
                await mutate(canonicalKey, response, { revalidate: false })
            }

            return response
        },
        [cache, mutate, revisions],
    )

    const {
        data: clientApplication,
        error,
        isLoading,
    } = useSwr(
        getClientApplicationSwrKey(applicationIdentifier),
        fetchClientApplication,
        {
            errorRetryCount: 0,
            // Skip only the first-load handoff to the populated UUID entry.
            // Cached routes start on that entry and revalidate normally.
            revalidateIfStale:
                revalidateIfStale &&
                applicationIdentifier === initialApplicationIdentifier,
        },
    )

    const applicationIdentity = useMemo(
        () => resolveApplicationIdentity(routeIdentifier, clientApplication),
        [clientApplication, routeIdentifier],
    )

    useEffect(() => {
        if (!applicationIdentity) {
            return
        }

        setResolvedIdentity((currentIdentity) => {
            if (
                currentIdentity?.routeIdentifier ===
                    applicationIdentity.routeIdentifier &&
                currentIdentity.numericId === applicationIdentity.numericId &&
                currentIdentity.uuid === applicationIdentity.uuid
            ) {
                return currentIdentity
            }

            return applicationIdentity
        })
    }, [applicationIdentity])

    return {
        applicationIdentity,
        clientApplication,
        loading: isLoading,
        error,
    }
}

export default useClientApplication
